import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Confetti from 'react-confetti';
import { CheckCircle, XCircle, AlertTriangle } from 'lucide-react';

type FeedbackType = 'success' | 'error' | 'warning' | null;

interface ScanFeedbackProps {
  type: FeedbackType;
  message: string;
  onComplete?: () => void;
  duration?: number;
}

const ScanFeedback: React.FC<ScanFeedbackProps> = ({ 
  type, 
  message, 
  onComplete,
  duration = 3000
}) => {
  const [showConfetti, setShowConfetti] = useState(false);
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  useEffect(() => {
    if (type === 'success') {
      setShowConfetti(true);
      const timer = setTimeout(() => {
        setShowConfetti(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [type]);

  useEffect(() => {
    if (type) {
      const timer = setTimeout(() => {
        if (onComplete) onComplete();
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [type, duration, onComplete]);

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircle className="w-16 h-16 text-green-500" />;
      case 'error':
        return <XCircle className="w-16 h-16 text-red-500" />;
      case 'warning':
        return <AlertTriangle className="w-16 h-16 text-yellow-500" />;
      default:
        return null;
    }
  };

  const getBackgroundColor = () => {
    switch (type) {
      case 'success':
        return 'bg-green-500/10';
      case 'error':
        return 'bg-red-500/10';
      case 'warning':
        return 'bg-yellow-500/10';
      default:
        return 'bg-transparent';
    }
  };

  const getBorderColor = () => {
    switch (type) {
      case 'success':
        return 'border-green-500/30';
      case 'error':
        return 'border-red-500/30';
      case 'warning':
        return 'border-yellow-500/30';
      default:
        return 'border-transparent';
    }
  };

  return (
    <AnimatePresence>
      {type && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          className="fixed inset-0 flex items-center justify-center z-50 p-4"
        >
          {showConfetti && type === 'success' && (
            <Confetti
              width={windowSize.width}
              height={windowSize.height}
              recycle={false}
              numberOfPieces={200}
              gravity={0.2}
            />
          )}
          <motion.div
            initial={{ y: 20 }}
            animate={{ y: 0 }}
            className={`${getBackgroundColor()} ${getBorderColor()} border-2 rounded-lg p-8 max-w-md w-full backdrop-blur-md shadow-xl flex flex-col items-center`}
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 200, damping: 10 }}
              className="mb-4"
            >
              {getIcon()}
            </motion.div>
            <h3 className={`text-xl font-bold mb-2 ${
              type === 'success' ? 'text-green-500' : 
              type === 'error' ? 'text-red-500' : 
              'text-yellow-500'
            }`}>
              {type === 'success' ? 'Succès!' : 
               type === 'error' ? 'Erreur!' : 
               'Attention!'}
            </h3>
            <p className="text-center text-white">{message}</p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ScanFeedback;
